import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { decodeQueryString } from '../queryStringUtils';
import { validateSearchTypes } from '../helpers/validateSearchTypes';
import type { SearchType, Zoom } from '../state/appState';

const DEFAULT_ZOOM = 4.1;

// The linkable keys for which the URL is the single source of truth. Derived
// from location.search on every render — no copy lives in app state, so nothing
// can drift.
export type LinkableSearchState = {
  filter: number[];
  searchType: SearchType;
  searchString: string;
};

export const parseLinkableSearchState = (search: string): LinkableSearchState => {
  const queryObject = decodeQueryString(search);

  // Default to nothing selected — all POI layers off (RBFF P0: keep the map
  // focused on fishing waters). A comma-separated `filter` param opts specific
  // POI type ids back in; an absent or empty param means none selected.
  const filter =
    queryObject.filter && queryObject.filter.length > 0
      ? queryObject.filter
          .split(',')
          .filter((it: unknown) => it)
          .map(Number)
      : [];
  const searchType = validateSearchTypes(queryObject.searchType) ? queryObject.searchType : '';
  const searchString = queryObject.searchString || '';
  return {
    filter,
    searchType,
    searchString,
  };
};

// Zoom is NOT URL-canonical: ?zoom= is parsed exactly once at startup;
// afterwards the live zoom is map-owned and navigation dropping the param has no effect.
export const parseInitialZoom = (search: string): Zoom => {
  const queryObject = decodeQueryString(search);
  return queryObject.zoom ? [Number(queryObject.zoom)] : [DEFAULT_ZOOM];
};

const useLinkableSearchState = (): LinkableSearchState => {
  const location = useLocation();
  return useMemo(() => parseLinkableSearchState(location.search), [location.search]);
};

export default useLinkableSearchState;
